import React, { useEffect, useRef, useState } from "react";

import { DatePicker as AntdDatePicker } from "antd";
import { RuleObject } from "antd/es/form";
import moment, { Moment } from "moment";
// eslint-disable-next-line import/no-extraneous-dependencies
import momentGenerateConfig from "rc-picker/lib/generate/moment";

import { generateRandomId, getLocalizedDateFormat, getMiddayMomentDate, parseInputRules, useTranslation } from "utils";

import { Form, Icon, InputLabel } from "components";

// @ts-expect-error: Create Moment-based Ant Design pickers via DatePicker.generatePicker(momentGenerateConfig).
// Wrappers are needed, so all pickers consistently use Moment in the app and tests,
// avoiding fallback dates (like 2070) and type errors in AntD v5.
// See docs: https://ant.design/docs/react/use-custom-date-library
const DatePicker = (AntdDatePicker as unknown).generatePicker<Moment>(momentGenerateConfig);
const { RangePicker: AntRangePicker } = DatePicker;

export type RangerPickerValueType = [moment.Moment | null, moment.Moment | null];

type RangePickerProps = {
  showNow?: boolean;
  showTime?: boolean | { format: string };
  initialValue?: RangerPickerValueType;
  dataTestId?: string;
  className?: string;
  name?: string | string[];
  label?: string | React.ReactNode;
  labelClassName?: string;
  message?: string;
  rules?: RuleObject[];
  disabled?: boolean;
  onChange?: (dates: RangerPickerValueType, dateStrings: [string, string]) => void;
  defaultValue?: RangerPickerValueType;
  defaultPickerValue?: RangerPickerValueType;
  value?: RangerPickerValueType;
  disabledDate?: (value: moment.Moment) => boolean;
  format?: string | string[];
  status?: "error" | "warning";
  onBlur?: (e: React.FocusEvent<HTMLElement>) => void;
  validateTrigger?: string | string[];
  help?: React.ReactNode;
};

type InputRef = {
  nativeElement: HTMLInputElement;
  focus: () => void;
  blur: () => void;
};

export const RangePicker = ({
  className = "",
  dataTestId,
  defaultPickerValue,
  disabled,
  disabledDate,
  format,
  initialValue,
  label,
  labelClassName = "",
  message,
  name,
  rules,
  showNow,
  showTime,
  status,
  value,
  onBlur = () => {},
  onChange = () => {},
  validateTrigger = "onBlur",
  help,
}: RangePickerProps) => {
  const { t } = useTranslation(["misc"]);
  const [rangeError, setRangeError] = useState<string | null>(null);
  const inputElement = useRef<InputRef>(null);
  const [hasBeenFocused, setHasBeenFocused] = useState(false);
  const [internalValue, setInternalValue] = useState(initialValue);
  const [internalDefaultValue, setInternalDefaultValue] = useState(() => {
    // If initialValue exists (even if [null, null]), use it
    if (initialValue) {
      return initialValue;
    }
    // Otherwise use defaultPickerValue or fallback to today
    return defaultPickerValue ?? [moment(), moment()];
  });

  const handleDisabledDate = (current: moment.Moment) => {
    if (disabledDate && disabledDate(current)) {
      return true;
    }

    return false;
  };

  const handleBlur = (e: React.FocusEvent<HTMLElement>) => {
    onBlur?.(e);
  };

  const handleChange = (dates: RangerPickerValueType, dateStrings: [string, string]) => {
    const newStartDate = dates[0];
    const newEndDate = dates[1];

    if (newStartDate && newEndDate && newEndDate.isBefore(newStartDate)) {
      // Set error message for invalid range
      setRangeError(t("rangePicker.invalidRangeError"));
    } else {
      // Clear error if range is valid
      setRangeError(null);
    }

    setInternalValue(dates);
    onChange(dates, dateStrings);
    setHasBeenFocused(false);
  };

  const handleCalendarChange = (dates: RangerPickerValueType, dateStrings: [string, string], info: { range: "start" | "end" }) => {
    const newStartDate = dates[0];
    const newEndDate = dates[1];

    if (info.range === "start") {
      // Clear the default picker end date when selecting a new start date
      const clearedDates: RangerPickerValueType = [newStartDate, null];
      setInternalDefaultValue(clearedDates);
      onChange(clearedDates, [dateStrings[0], ""]);
    }
    if (info.range === "end") {
      // Clear the default picker start date when selecting a new end date
      const clearedDates: RangerPickerValueType = [null, newEndDate];
      setInternalDefaultValue(clearedDates);
      onChange(clearedDates, ["", dateStrings[1]]);
    }

    setRangeError(null);
    setHasBeenFocused(false);
  };

  const handleLabelClick = () => {
    setHasBeenFocused(true);
    inputElement.current?.focus();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setHasBeenFocused(false);
    }
  };

  const normalizeDates = (dates: [Moment, Moment]) => {
    const startDate = getMiddayMomentDate(dates[0]);
    const endDate = getMiddayMomentDate(dates[1]);
    return [startDate, endDate];
  };

  const active = !!(hasBeenFocused || internalValue || value);
  const required = rules && rules[0] && rules[0].required;
  const hasError = rangeError || status === "error";

  const keyRef = useRef<string>(generateRandomId());
  const key = keyRef.current;

  // Update internalDefaultValue when props change
  useEffect(() => {
    // Priority: internalValue > defaultPickerValue > [moment(), moment()]
    const start = initialValue ? initialValue[0] : null;
    const end = initialValue ? initialValue[1] : null;

    const startDefault = defaultPickerValue ? defaultPickerValue[0] : null;
    const endDefault = defaultPickerValue ? defaultPickerValue[1] : null;

    if (start || end) {
      setInternalDefaultValue([start, end]);
    } else if (startDefault || endDefault) {
      setInternalDefaultValue([startDefault, endDefault]);
    } else {
      setInternalDefaultValue([moment(), moment()]);
    }
  }, [initialValue, defaultPickerValue]);

  return (
    <Form.Item
      className={`floating-input ${hasBeenFocused ? "active" : ""} ${disabled ? "disabled" : "message"} ${className}`}
      colon={false}
      label={
        <InputLabel
          active={active}
          required={required}
          label={label}
          className={labelClassName}
          dataTestId={dataTestId}
          handleClick={handleLabelClick}
          id={`${key}-label`}
        />
      }
      name={name}
      normalize={normalizeDates}
      rules={parseInputRules(rules, message)}
      initialValue={initialValue}
      {...(hasError ? { validateStatus: "error" as const } : {})}
      {...(rangeError || help ? { help: rangeError || help } : {})}
      validateTrigger={validateTrigger}
    >
      <AntRangePicker
        allowClear={false}
        defaultPickerValue={internalDefaultValue}
        disabled={disabled}
        disabledDate={handleDisabledDate}
        format={getLocalizedDateFormat() ?? format}
        onBlur={handleBlur}
        onChange={handleChange}
        onCalendarChange={handleCalendarChange}
        onFocus={() => setHasBeenFocused(true)}
        onOpenChange={handleOpenChange}
        open={hasBeenFocused}
        placeholder={[t("rangePicker.startDate"), t("rangePicker.endDate")]}
        placement="bottomRight"
        ref={inputElement}
        showNow={showNow}
        showTime={showTime}
        status={hasError ? "error" : status}
        suffixIcon={<Icon iconName="tabler/calendar" />}
        value={value ? [moment(value[0]), moment(value[1])] : undefined}
        data-testid={`${dataTestId}-picker`}
        id={key}
        key={key}
        aria-labelledby={`${key}-label`}
        separator="—"
        classNames={{
          popup: {
            root: "range-picker-dropdown",
          },
        }}
        picker="date"
      />
    </Form.Item>
  );
};
